import React from 'react';
import { useTheme } from 'styled-components';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Icon } from '../Icon/Icon';
import { MATERIAL_ICONS } from '../Icon/icons';
import { MAIN_BRAND_COLOURS } from '../../fundamentals/colours/variants';
import { ColourAssignment, IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { ResponsiveSpaceContainer } from '../../fundamentals/spaces/responsive/SpaceContainerResponsive';
import { IResponsiveGutterAsMarginProps } from '../../fundamentals/spaces/responsive/spaceResponsive';

export const FIVE_STAR_VARIANTS = {
  DEFAULT: 'default',
  NEUTRAL: 'neutral',
  ...MAIN_BRAND_COLOURS,
  YELLOW: 'yellow'
} as const;

export type FiveStarVariant = (typeof FIVE_STAR_VARIANTS)[keyof typeof FIVE_STAR_VARIANTS];

export const fiveStarColours = ({
  text,
  misc,
  palette,
  accent,
  primary
}: IColoursCollection): { fiveStarColours: Record<FiveStarVariant, ColourAssignment> } => {
  const makeColour = (active: string) => ({ text: active, background: palette.neutral50 });
  return {
    fiveStarColours: {
      [FIVE_STAR_VARIANTS.DEFAULT]: makeColour(text.default),
      [FIVE_STAR_VARIANTS.NEUTRAL]: makeColour(palette.neutral),
      [FIVE_STAR_VARIANTS.ACCENT]: makeColour(accent.colour),
      [FIVE_STAR_VARIANTS.PRIMARY]: makeColour(primary.colour),
      [FIVE_STAR_VARIANTS.YELLOW]: makeColour(misc.yellow)
    }
  };
};

export type FiveStarSize =
  | typeof SIZES.MICRO
  | typeof SIZES.XXX_SMALL
  | typeof SIZES.XX_SMALL
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL
  | typeof SIZES.MEDIUM
  | typeof SIZES.LARGE;

export type FiveStarStars = 0 | 1 | 2 | 3 | 4 | 5;

export interface IFiveStarProps extends IResponsiveGutterAsMarginProps {
  size?: FiveStarSize;
  variant?: FiveStarVariant;
  stars?: FiveStarStars;
  of?: FiveStarStars;
}

export const FiveStar: React.FC<IFiveStarProps> = ({
  stars = 0,
  of = 5,
  size = SIZES.MICRO,
  variant = FIVE_STAR_VARIANTS.DEFAULT,
  ...rest
}: IFiveStarProps) => {
  const { generateTestId } = useTestId(rest, 'FiveStar');
  const theme = useTheme();
  const { text, background } = theme.fiveStarColours[variant];

  const safeOf = Math.max(of, 2);
  const safeStars = Math.min(stars, safeOf);

  return (
    <ResponsiveSpaceContainer {...generateTestId('stars')} {...rest}>
      {[...Array.from({ length: safeOf }).keys()].map((currentNumber) => (
        <Icon key={currentNumber} colour={currentNumber < safeStars ? text : background} name={MATERIAL_ICONS.Star} size={size} />
      ))}
    </ResponsiveSpaceContainer>
  );
};
